import { useSearchParams } from "react-router-dom";
import { adicionarMeses, hojeISO } from "../../../shared/lib/date";
import { addDays, isoDate, parseIsoDate } from "../utils/calendarViews";

export const CALENDAR_VIEWS = ["mes", "semana", "lista"];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const storageKey = (userId) => `medhub:calendario_visao:${userId ?? "anon"}`;

function readStoredView(userId) {
  try {
    const stored = window.localStorage.getItem(storageKey(userId));
    return CALENDAR_VIEWS.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

function storeView(userId, view) {
  try {
    window.localStorage.setItem(storageKey(userId), view);
  } catch {
    // localStorage indisponível (modo privado restrito): só não lembra a visão.
  }
}

// No celular a Lista é a visão mais útil: a grade de 7 colunas vira só pontos.
function isNarrowScreen() {
  try {
    return window.matchMedia("(max-width: 47.99em)").matches;
  } catch {
    return false;
  }
}

function validDate(value) {
  if (!ISO_DATE.test(value ?? "")) return null;
  const date = parseIsoDate(value);
  return Number.isNaN(date.getTime()) ? null : value;
}

// Visão (Mês/Semana/Lista) e data de referência do Calendário, refletidas na URL
// (`?visao=semana&data=2026-11-10`) para que o "voltar" do navegador desfaça a navegação.
// Sem `visao` na URL vale a última escolhida neste navegador (por usuário) e, se nunca
// houve escolha, Lista em tela estreita e Mês no resto. Sem `data`, hoje.
export function useCalendarView(userId) {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = hojeISO();
  const urlView = searchParams.get("visao");
  const view = CALENDAR_VIEWS.includes(urlView) ? urlView : readStoredView(userId) ?? (isNarrowScreen() ? "lista" : "mes");
  const date = validDate(searchParams.get("data")) ?? today;
  const anchor = parseIsoDate(date);
  const year = anchor.getFullYear();
  const month = anchor.getMonth() + 1;

  const go = (nextView, nextDate) => setSearchParams({ visao: nextView, data: nextDate });
  const shiftMonth = (delta) => {
    const next = adicionarMeses(year, month, delta);
    go(view, isoDate(next.ano, next.mes, 1));
  };
  const step = (delta) => (view === "semana" ? go(view, addDays(date, 7 * delta)) : shiftMonth(delta));

  return {
    view,
    date,
    year,
    month,
    today,
    setView: (nextView) => {
      storeView(userId, nextView);
      go(nextView, date);
    },
    previous: () => step(-1),
    next: () => step(1),
    goToday: () => go(view, today),
  };
}
